import { Component, DestroyRef, Injector, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, zip } from "rxjs";
import { filter, startWith, take } from 'rxjs/operators';
import { User, UserTab } from "../../../auth/models";
import { Company } from '../../../auth/models/company';
import { ConfirmModalComponent } from '../../../common/components/confirm-modal/confirm-modal.component';
import { ToastService } from '../../../main/services';
import { Supplier } from '../../models/supplier/supplier';
import { Tender } from '../../models/tender';
import { TenderService, TenderSuppliersService } from '../../services';
import { CreateSupplierFormComponent } from "../suppliers";
import {
    AddInvitedSuppliersFormModalComponent,
    CreateSupplierBunchFileModalComponent,
    CreateSupplierBunchTableModalComponent,
    CreateSupplierFormModalComponent
} from './../create-tender-suppliers';

@Component({
    selector: 'jft-tender-suppliers',
    templateUrl: './create-tender-suppliers.component.html',
    styleUrls: ['./create-tender-suppliers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateTenderSuppliersComponent implements OnInit {

    public tender: Tender;
    public supplier: Supplier;
    public suppliers: Company[] = [];
    public tenderInvitedSupplierUsers: User[] = [];
    public filteredTenderInvitedSupplierUsers: User[] = [];
    public searchTerm: string = '';
    public reason = 'At least 2 suppliers should be invited';
    public addedSupplierObservables = [];
    public availableTabs: UserTab[] = [];
    public selectedTags: any[] = [];
    public tags: any[] = [];
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private tenderService: TenderService,
        private supplierService: TenderSuppliersService,
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private injector: Injector,
        private ngbModal: NgbModal
    ) {
    }

    public ngOnInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.snapshot.data;
        this.availableTabs = data['availableTabs'];

        this.tender = parentData['tender'];
        this.tenderInvitedSupplierUsers = data['tenderInvitedSupplierUsers'].sort((a, b) => a.company.name.localeCompare(b.company.name));
        this.filteredTenderInvitedSupplierUsers = [...this.tenderInvitedSupplierUsers];
        this.setSuppliers();
        this.initAvailableTags();

        // Check active child route on load and on change
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            startWith(null), // Trigger on load
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            const child = this.route.firstChild;
            if (child) {
                // One read per navigation: a lasting subscription here piled up one per visit.
                child.url.pipe(take(1)).subscribe(urlSegment => {
                    if (urlSegment.length > 1 && urlSegment[1].path === 'edit') {
                        const supplierId = parseInt(urlSegment[0].path);
                        // Find the supplier in the list
                        const supplierUser = this.tenderInvitedSupplierUsers.find(u => u.id === supplierId);
                        if (supplierUser) {
                            this.editSupplier(supplierUser);
                        } else {
                            // If not found in loaded list (unlikely if resolver loaded all), fallback or error.
                            // assuming resolver loaded invited suppliers.
                            this.toast.error('Supplier not found');
                            this.clearRoute();
                        }
                    }
                });
            }
        });
    }

    private initAvailableTags() {
        this.availableTabs.forEach((c, i) => {
            this.tags.push({ id: c.id, name: c.name });
        });
    }

    private setSuppliers() {
        let suppliers = this.route.snapshot.data['suppliers'];
        this.suppliers = suppliers.filter((user: User) => {
            return !this.suppliers.find((supplier: any) => {
                return user.id == supplier.id;
            });
        });
    }

    public getUniqueCompanyCount(): number {
        const uniqueIds = new Set(this.tenderInvitedSupplierUsers.map(user => user.company.id));
        return uniqueIds.size;
    }

    public getTotalUserCount(): number {
        return this.tenderInvitedSupplierUsers.length;
    }

    public deleteSupplier($event: Event, supplier: Supplier) {
        this.supplierService.delete(this.tender, supplier).subscribe(() => {
            //let index = this.tenderSuppliers.indexOf(supplier);
            //this.tenderSuppliers.splice(index, 1);
            this.refreshSuppliers();
            this.setSuppliers();
            this.toast.success(`${supplier['firstName']} ${supplier['lastName']} from ${supplier['company']['name']} removed`);
        }, (e) => {
            this.toast.error(e);
        });
    }

    public deleteAllSuppliers(): void {
        if (this.tenderInvitedSupplierUsers.length === 0) {
            return;
        }

        // Create an array of delete observables for all suppliers
        // Cast User objects to Supplier type since the service only needs the id property
        const deleteObservables = this.tenderInvitedSupplierUsers.map(supplier =>
            this.supplierService.delete(this.tender, supplier as any)
        );

        // Execute all delete operations in parallel
        forkJoin(deleteObservables).subscribe({
            next: () => {
                this.toast.success(`All ${this.tenderInvitedSupplierUsers.length} suppliers have been removed from the tender`);
                this.refreshSuppliers();
                this.setSuppliers();
            },
            error: (error) => {
                this.toast.error('Failed to delete some suppliers. Please try again.');
                console.error('Delete all suppliers error:', error);
                // Refresh to show current state
                this.refreshSuppliers();
                this.setSuppliers();
            }
        });
    }

    public stepBack() {
        this.tenderService.stepBack(this.tender)
            .subscribe((tender: Tender) => {
                this.tender = tender;
                this.router.navigateByUrl(`/tenders/new/${tender.id}/locations`);
            }, (e) => {
                this.toast.error(e);
            });
    }

    public submitSuppliers() {

        this.tenderService.submitSuppliers(this.tender).subscribe((tender: Tender) => {
            this.tender = tender;
            this.toast.success('Tender updated');
            this.router.navigate(['tenders', 'new', tender.id, 'review']);
        }, (err) => {
            this.toast.error(err);
        });
    }

    public deleteTender(): void {
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Delete Tender';
        modalRef.componentInstance.text = 'Are you sure you want to delete this tender draft? This action cannot be undone.';

        modalRef.result.then((result) => {
            if (result) {
                this.tenderService.delete(this.tender).subscribe({
                    next: () => {
                        this.toast.success('Tender deleted successfully');
                        this.router.navigate(['/tenders']);
                    },
                    error: (e) => {
                        this.toast.error(e);
                    }
                });
            }
        }, () => { });
    }

    public createSupplierPasteExel() {
        this.ngbModal.open(CreateSupplierBunchTableModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
                { provide: Supplier, useValue: this.supplier },
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true
        }).result.then(() => {
            this.refreshSuppliers();
        }, (error) => {

        });
    }

    public createSupplierUploadExel() {
        this.ngbModal.open(CreateSupplierBunchFileModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
                { provide: Supplier, useValue: this.supplier },
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true
        }).result.then(() => {
            this.refreshSuppliers();
        }, (error) => {
        });
    }

    public createSupplierManually() {
        const modal: NgbModalRef = this.ngbModal.open(CreateSupplierFormModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
                { provide: Supplier, useValue: this.supplier },
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true
        })
        modal.result.then((data) => {
            // Supplier is already added to tender by the backend, just refresh the list
            this.refreshSuppliers();
        }, (error) => { });
        modal.componentInstance.availableTags = this.availableTabs;
    }

    public addSuppliersFromContacts() {
        const modal: NgbModalRef = this.ngbModal.open(AddInvitedSuppliersFormModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
            ], this.injector),
            fullscreen: true,
            backdrop: 'static',
            centered: true
        })
        modal.result.then((data) => {
            this.addSuppliersToTender(data.users);
        }, (error) => { });
        modal.componentInstance.availableTags = this.availableTabs;
    }

    private addSuppliersToTender(selectedUsers) {
        this.addedSupplierObservables = [];
        selectedUsers.forEach((supplier) => {
            this.addedSupplierObservables.push(this.supplierService.add(this.tender, supplier));
        });

        zip(...this.addedSupplierObservables).subscribe(() => {
            this.toast.success(`Suppliers have been added`);
            this.refreshSuppliers();
        }, (e) => {
            this.toast.error(e);
        });
    }

    private refreshSuppliers(filter_tags = []) {
        this.supplierService.fetch(this.tender.id).subscribe((suppliers: Supplier[]) => {
            //this.tenderSuppliers = suppliers;
            this.setSuppliers();
        })

        this.supplierService.fetchInvitedSupplierUsers(this.tender.id, filter_tags).subscribe((users: User[]) => {
            this.tenderInvitedSupplierUsers = users;
            this.applySearch();
        })
    }

    public onSearchChange() {
        this.applySearch();
    }

    private applySearch() {
        if (!this.searchTerm.trim()) {
            this.filteredTenderInvitedSupplierUsers = [...this.tenderInvitedSupplierUsers];
        } else {
            const term = this.searchTerm.toLowerCase().trim();
            this.filteredTenderInvitedSupplierUsers = this.tenderInvitedSupplierUsers.filter(supplier =>
                supplier.company.name.toLowerCase().includes(term) ||
                supplier.firstName.toLowerCase().includes(term) ||
                supplier.lastName.toLowerCase().includes(term) ||
                supplier.email.toLowerCase().includes(term) ||
                (supplier.supplierAlias && supplier.supplierAlias.toLowerCase().includes(term))
            );
        }
    }

    public clickTag(tag) {
        // this.refreshSuppliers([tag.id]);
        // this.selectedTags.push(tag);
    }

    public tagChange(tags) {
        this.refreshSuppliers(tags.map(item => item.id));
    }

    public editSupplier(supplier: any) {
        if (!this.router.url.includes(`/${supplier.id}/edit`)) {
            this.router.navigate([supplier.id, 'edit'], { relativeTo: this.route });
            return;
        }

        let supplierData = supplier['company']
        supplierData['contact'] = supplier;

        const modal: NgbModalRef = this.ngbModal.open(CreateSupplierFormComponent, {
            injector: Injector.create([
                { provide: Supplier, useValue: supplierData },
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true
        })
        modal.result.then((data) => {
            this.refreshSuppliers();
            this.clearRoute();
        }, (error) => { this.clearRoute(); });
        modal.componentInstance.formType = 'edit';
        modal.componentInstance.availableTags = this.availableTabs;
    }
    private clearRoute() {
        this.router.navigate(['.'], { relativeTo: this.route });
    }
}
